// FamilyDigest — Feb 2026 (Randy). Weekly (or 30-day) rollup card that lives
// as its own collapsible sub-button inside the AI Calendar frame. Shows
// per-member chores done + points earned + top current streak, plus the
// household totals and recent delivered redemptions. Owner sees this by
// default; promoted parents (silo.parent_ids) can access it too from their
// own Family View (that wiring is separate — this component just renders
// whatever /family-digest gives back).
//
// Teenager aesthetic per Randy — muted chip palette, no cutesy borders,
// tabular numerics, roll-up cards over kid-oriented illustrations.
import { useEffect, useState } from "react";
import { Trophy, Fire, Coin, Spinner, CalendarBlank } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

export default function FamilyDigest({ siloId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState(null);

  // pt89W — Digest labels honour the silo's Re-Themer. The provider is
  // seeded by CalendarSection above, so hooks here read that context.
  const tChoresDone   = useThemeLabel(T.THEME_KEY_DIGEST_TOTAL_CHORES);
  const tPointsEarned = useThemeLabel(T.THEME_KEY_DIGEST_TOTAL_POINTS);
  const tRewardsDone  = useThemeLabel(T.THEME_KEY_DIGEST_TOTAL_REWARDS);
  const tRowUnit      = useThemeLabel(T.THEME_KEY_DIGEST_ROW_UNIT);
  const tEmpty        = useThemeLabel(T.THEME_KEY_DIGEST_EMPTY);
  const tRedeHeader   = useThemeLabel(T.THEME_KEY_DIGEST_REDEMPTIONS_HEADER);
  const tRoleOwner    = useThemeLabel(T.THEME_KEY_DIGEST_ROLE_OWNER);
  const tRoleParent   = useThemeLabel(T.THEME_KEY_DIGEST_ROLE_PARENT);
  const tRoleFamily   = useThemeLabel(T.THEME_KEY_DIGEST_ROLE_FAMILY);
  const tRoleKid      = useThemeLabel(T.THEME_KEY_DIGEST_ROLE_KID);

  const ROLE_TAG = {
    owner:  { label: tRoleOwner,  cls: "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]" },
    parent: { label: tRoleParent, cls: "bg-[#2C6E92] text-white border-[#2C6E92]" },
    family: { label: tRoleFamily, cls: "bg-black/5 text-[#1C1B1A] border-black/10" },
    kid:    { label: tRoleKid,    cls: "bg-[#F8F1E2] text-[#8A6A24] border-[#EAD9AE]" },
  };

  const load = async (d) => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/calendar/${siloId}/family-digest`, { params: { days: d } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't load the digest.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [siloId, days]);

  if (loading) {
    return <div className="text-xs text-[#6B6A68] flex items-center gap-1.5 py-3" data-testid="family-digest-loading"><Spinner size={12} className="animate-spin" /> Loading digest…</div>;
  }
  if (err || !data) {
    return <div className="text-xs text-[#A55A48] italic py-2" data-testid="family-digest-error">{err || "No data."}</div>;
  }

  return (
    <div className="space-y-3" data-testid="family-digest">
      {/* range toggle + hairline totals bar */}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
        <CalendarBlank size={11} weight="duotone" />
        <span>{data.window_start} → {data.window_end}</span>
        <span className="flex-1" />
        <div className="flex rounded-md border hairline overflow-hidden" data-testid="family-digest-range">
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`family-digest-range-${d}`}
              className={`px-2 py-1 text-[10px] ${days === d ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="family-digest-totals">
        <TotalCard icon={<Trophy size={14} weight="duotone" className="text-[#C19A4A]" />} label={tChoresDone} value={data.totals.chores_done} />
        <TotalCard icon={<Coin size={14} weight="duotone" className="text-[#8A6A24]" />} label={tPointsEarned} value={data.totals.points_earned} />
        <TotalCard icon={<Fire size={14} weight="duotone" className="text-[#C16E5A]" />} label={tRewardsDone} value={data.totals.redemptions_delivered} />
      </div>

      {/* per-member breakdown */}
      <div className="border hairline rounded-md bg-white" data-testid="family-digest-rows">
        {data.rows.length === 0 ? (
          <div className="text-xs text-[#6B6A68] italic p-3">
            {tEmpty} <span className="text-[#8A8A88]">Last {days} days.</span>
          </div>
        ) : (
          data.rows.map((r, i) => {
            const tag = ROLE_TAG[r.kind] || ROLE_TAG.family;
            return (
              <div key={r.member_id + i} className="flex items-center gap-2 px-3 py-2 border-b hairline last:border-b-0" data-testid={`family-digest-row-${r.member_id}`}>
                <span className="text-lg leading-none">{r.emoji}</span>
                <span className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-[#1C1B1A] truncate">{r.name}</span>
                    <span className={`text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${tag.cls}`}>{tag.label}</span>
                  </div>
                  <div className="text-[10px] text-[#6B6A68]">{r.chores_done} {tRowUnit} · {r.points_earned} pts{r.top_streak > 0 ? ` · 🔥 ${r.top_streak}` : ""}</div>
                </span>
              </div>
            );
          })
        )}
      </div>

      {(data.redemptions || []).length > 0 && (
        <details className="text-[11px]" data-testid="family-digest-redemptions">
          <summary className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] cursor-pointer">{tRedeHeader}</summary>
          <div className="mt-1 space-y-0.5">
            {data.redemptions.map((rd, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[#6B6A68]">
                <span>{rd.reward_emoji}</span>
                <span className="flex-1 truncate">{rd.reward_name} · {rd.redeemer_name}</span>
                <span className="text-[10px] text-[#8A6A24]">{(rd.delivered_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function TotalCard({ icon, label, value }) {
  return (
    <div className="bg-white border hairline rounded-md px-2.5 py-2 flex flex-col gap-0.5" data-testid={`family-digest-total-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-1.5">{icon}<span className="text-[9px] uppercase tracking-[0.14em] text-[#6B6A68]">{label}</span></div>
      <span className="text-lg font-medium text-[#1C1B1A] tabular-nums leading-none">{value}</span>
    </div>
  );
}
